import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Zap, Building2, Shield, Briefcase, Scale, ArrowUpRight } from "lucide-react";
import { fmtMoney } from "../../lib/money";

/**
 * QENVA · Opportunités Guardian™ — 4 detailed opportunity cards.
 *
 *  ┌─────────────────────┐
 *  │ 📄                  │
 *  │                     │
 *  │ Renégociation bail  │
 *  │ commercial          │
 *  │ ────                │
 *  │ VALEUR PRÉSERVÉE™  │
 *  │ 28 000 $            │
 *  │                     │
 *  │ CONFIANCE [▓▓▓░] 88%│
 *  │                     │
 *  │ DIFFICULTÉ          │
 *  │ Facile (vert)       │
 *  └─────────────────────┘
 *
 * Each card opens the corresponding alert/CrossCheck comparison on click.
 */
const CATEGORY_ICON = {
  finding:        Scale,
  recommendation: Briefcase,
  insurance:      Shield,
  lease:          Building2,
  default:        Briefcase,
};

export default function OpportunitiesGuardianGrid({ opportunities, onCrossCheck, testId = "opportunities-guardian" }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const items = opportunities || [];

  const DIFFICULTY_DEF = {
    easy:   { label: t("opportunities_guardian.difficulty_easy"),   color: "text-emerald-600" },
    medium: { label: t("opportunities_guardian.difficulty_medium"), color: "text-amber-600" },
    hard:   { label: t("opportunities_guardian.difficulty_hard"),   color: "text-rose-600" },
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-6" data-testid={testId}>
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber-500" />
          <h2 className="font-display font-bold text-qenva-navy text-[16px] tracking-tight">
            {t("opportunities_guardian.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => (onCrossCheck ? onCrossCheck() : navigate("/comparisons"))}
          data-testid={`${testId}-crosscheck-link`}
          className="text-qenva-blue text-[12px] font-semibold hover:underline inline-flex items-center gap-1"
        >
          CrossCheck™ <ArrowUpRight size={11} />
        </button>
      </header>

      {items.length === 0 ? (
        <p className="text-zinc-400 text-[13px] text-center py-8" data-testid={`${testId}-empty`}>
          {t("opportunities_guardian.empty")}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={`${testId}-grid`}>
          {items.map((opp, i) => {
            const Icon = CATEGORY_ICON[opp.category] || CATEGORY_ICON.default;
            const conf = Math.max(0, Math.min(100, opp.confidence_pct || 0));
            const diff = DIFFICULTY_DEF[opp.difficulty] || DIFFICULTY_DEF.medium;
            return (
              <article
                key={opp.alert_id || i}
                className="rounded-xl border border-zinc-200 bg-white p-4 hover:shadow-sm hover:border-qenva-blue/30 transition-all cursor-pointer"
                onClick={() => opp.alert_id && navigate(`/alerts?id=${opp.alert_id}`)}
                data-testid={`${testId}-item-${i}`}
              >
                <div className="w-9 h-9 rounded-lg bg-zinc-100 grid place-items-center text-zinc-700 mb-4">
                  <Icon size={15} strokeWidth={1.75} />
                </div>
                <p className="font-display font-bold text-qenva-navy text-[13.5px] leading-tight mb-3 line-clamp-2 min-h-[34px]" title={opp.title}>
                  {opp.title}
                </p>
                <hr className="border-zinc-200 mb-3" />

                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-500">{t("opportunities_guardian.preserved_value")}</p>
                <p className="font-display font-bold text-emerald-600 text-[18px] tabular-nums leading-none mb-3" data-testid={`${testId}-item-${i}-value`}>
                  {fmtMoney(opp.preserved_value, opp.preserved_currency)}
                </p>

                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-500 mb-1">{t("opportunities_guardian.confidence")}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-1.5 rounded-full bg-zinc-200 overflow-hidden">
                    <div className="h-full bg-qenva-blue rounded-full" style={{ width: `${conf}%` }} />
                  </div>
                  <span className="text-[11px] font-bold text-qenva-blue tabular-nums shrink-0">{conf}%</span>
                </div>

                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-500">{t("opportunities_guardian.difficulty")}</p>
                <p className={`text-[12.5px] font-bold ${diff.color}`} data-testid={`${testId}-item-${i}-difficulty`}>
                  {diff.label}
                </p>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
